import { Archive, ArrowRight, CalendarDays, Copy, Plus } from 'lucide-react'
import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useDuplicateEvent, useEvents, useMe } from '../../api/hooks'
import type { Event } from '../../api/types'
import { Badge, Button, CardSkeleton, DrillLink, EmptyState, PageHeader, Progress, Stat } from '../../components/ui'
import { useToast } from '../../components/ui/toast'
import { EVENT_STATUS_LABEL, EVENT_TYPE_LABEL, fmtDate, fmtTime, num, pct } from '../../lib/format'

const STATUS_TONE = { UTKAST: 'neutral', AKTIVT: 'success', STANGT: 'warning', ARKIVERAT: 'neutral' } as const

export function EventsPage() {
  const [showArchived, setShowArchived] = useState(false)
  const events = useEvents(showArchived)
  const me = useMe()
  const isManager = me.data?.role !== 'RADGIVARE'
  const ORDER: Record<Event['status'], number> = { AKTIVT: 0, STANGT: 1, UTKAST: 2, ARKIVERAT: 3 }
  const list = [...(events.data ?? [])].sort((a, b) => ORDER[a.status] - ORDER[b.status] || a.starts_at.localeCompare(b.starts_at))
  const active = list.filter((e) => e.status === 'AKTIVT')
  const totals = active.reduce(
    (a, e) => ({ registered: a.registered + e.counts.registered, capacity: a.capacity + e.capacity, categories: a.categories + e.counts.categories, sent: a.sent + e.counts.sent }),
    { registered: 0, capacity: 0, categories: 0, sent: 0 },
  )

  return (
    <>
      <PageHeader
        title="Event"
        subtitle={isManager ? 'Alla event inom Wealth Management' : 'Event där ditt team har inbjudna kunder'}
        actions={
          <>
            <Button variant="ghost" size="sm" icon={<Archive className="size-4" />} onClick={() => setShowArchived((s) => !s)}>
              {showArchived ? 'Dölj arkiverade' : 'Visa arkiverade'}
            </Button>
            {isManager && (
              <Link to="/event/nytt"><Button variant="primary" icon={<Plus className="size-4" />}>Nytt event</Button></Link>
            )}
          </>
        }
      />

      {isManager && (
        <div className="mb-6 grid grid-cols-2 gap-3 md:grid-cols-4">
          <Stat label="Aktiva event" value={active.length} tone="navy" />
          <Stat label="Anmälda" value={totals.registered} sub={`av ${num(totals.capacity)} platser`} delay={40} />
          <Stat label="Kategorier" value={totals.categories} sub="unika anmälningslänkar" delay={80} />
          <Stat label="Skickade inbjudningar" value={totals.sent} delay={120} />
        </div>
      )}

      {events.isLoading ? (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3"><CardSkeleton /><CardSkeleton /><CardSkeleton /></div>
      ) : list.length === 0 ? (
        <EmptyState
          icon={<CalendarDays className="size-8" />}
          title="Inga event ännu"
          description="Skapa ett event, generera kategorier per rådgivarteam och skicka inbjudningar – allt från ett ställe."
          action={isManager && <Link to="/event/nytt"><Button variant="primary">Skapa första eventet</Button></Link>}
        />
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {list.map((e, i) => <EventCard key={e.id} event={e} canDuplicate={isManager} index={i} />)}
        </div>
      )}
    </>
  )
}

function EventCard({ event: e, canDuplicate, index }: { event: Event; canDuplicate: boolean; index: number }) {
  const nav = useNavigate()
  const dup = useDuplicateEvent()
  const toast = useToast()
  const fill = e.capacity ? e.counts.registered / e.capacity : 0
  return (
    <article
      onClick={() => nav(`/event/${e.id}`)}
      onKeyDown={(ev) => ev.key === 'Enter' && nav(`/event/${e.id}`)}
      tabIndex={0}
      role="link"
      className="card-interactive group relative flex flex-col p-5 animate-fade-up"
      style={{ animationDelay: `${Math.min(index, 8) * 50}ms` }}
    >
      <div className="mb-3 flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <Badge tone={STATUS_TONE[e.status]} dot>{EVENT_STATUS_LABEL[e.status]}</Badge>
          <span className="text-xs text-ink-500">{EVENT_TYPE_LABEL[e.event_type]}</span>
        </div>
        {canDuplicate && (
          <button
            title="Kopiera event"
            aria-label="Kopiera event"
            disabled={dup.isPending}
            onClick={(ev) => {
              ev.stopPropagation()
              dup.mutate(e.id, {
                onSuccess: (n) => toast.push('success', `Kopia skapad: "${n.name}" ligger nu som utkast`),
                onError: (err) => toast.push('error', err.message),
              })
            }}
            className="rounded-md p-1 text-ink-400 transition-colors hover:bg-navy-50 hover:text-navy-800 disabled:opacity-40"
          >
            <Copy className="size-4" />
          </button>
        )}
      </div>
      <h2 className="text-base leading-snug transition-colors duration-[140ms] group-hover:text-navy-800">{e.name}</h2>
      <p className="mt-1 text-sm text-ink-500">{fmtDate(e.starts_at)} kl. {fmtTime(e.starts_at)} · {e.location}</p>

      <div className="mt-5 grid grid-cols-3 gap-3 text-sm">
        <div><div className="text-xs text-ink-500">Kategorier</div><div className="font-semibold tabular-nums text-ink-900"><DrillLink to={`/event/${e.id}/kategorier`}>{e.counts.categories}</DrillLink></div></div>
        <div><div className="text-xs text-ink-500">Anmälda</div><div className="font-semibold tabular-nums text-ink-900"><DrillLink to={`/event/${e.id}/deltagare`}>{e.counts.registered}</DrillLink> <span className="font-normal text-ink-500">/ {e.capacity}</span></div></div>
        <div><div className="text-xs text-ink-500">Skickade</div><div className="font-semibold tabular-nums text-ink-900"><DrillLink to={`/event/${e.id}/utskick`}>{e.counts.sent}</DrillLink></div></div>
      </div>
      <div className="mt-3 flex items-center gap-3">
        <Progress value={fill} tone={fill >= 1 ? 'danger' : fill > 0.8 ? 'warning' : 'navy'} className="flex-1" />
        <span className="text-xs tabular-nums text-ink-500">{pct(fill)}</span>
      </div>
      <div className="mt-4 flex items-center justify-between border-t border-line-100 pt-3 text-xs text-ink-500">
        <span>{e.created_by_name ? `Skapad av ${e.created_by_name}` : ''}</span>
        <span className="inline-flex items-center gap-1 font-medium text-navy-700 transition-all duration-[160ms] group-hover:gap-2 group-hover:text-navy-900">
          Öppna <ArrowRight className="size-3.5 transition-transform duration-[160ms] group-hover:translate-x-0.5" />
        </span>
      </div>
    </article>
  )
}
